// src/components/auth/LoginModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import '../../styles/layout/Modal.css';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onSwitchToRegister?: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onSwitchToRegister
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const firstFieldRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen && !showForgotPassword) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose, showForgotPassword]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setPassword('');
            setError('');
            setShowPassword(false);
        }
    }, [isOpen]);

    // Move focus into the dialog so keyboard and screen-reader users start inside it.
    useEffect(() => {
        if (isOpen) {
            const timer = window.setTimeout(() => firstFieldRef.current?.focus(), 60);
            return () => window.clearTimeout(timer);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Which world to play is no longer decided here. Signing in gets an account;
            // the world selection page is where a character is chosen or created.
            onSuccess();
            onClose();
        } catch (error: any) {
            if (error.code === 'auth/user-not-found') {
                setError('Kasutajat ei leitud');
            } else if (error.code === 'auth/wrong-password') {
                setError('Vale parool');
            } else if (error.code === 'auth/invalid-credential') {
                setError('Vale e-post või parool');
            } else if (error.code === 'auth/invalid-email') {
                setError('Vigane e-posti aadress');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Liiga palju ebaõnnestunud katseid. Proovi hiljem uuesti.');
            } else {
                setError('Sisselogimine ebaõnnestus');
            }
            setLoading(false);
        }
    };

    return (
        <>
            <div
                className="vm-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="vm-login-title"
                onMouseDown={(e) => {
                    // Close only on the backdrop itself, never on a drag that ends there.
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                <div className="vm-modal__panel">
                    <button
                        type="button"
                        className="vm-modal__close"
                        onClick={onClose}
                        aria-label="Sulge"
                    >
                        ×
                    </button>

                    <div className="vm-modal__head">
                        <p className="vm-modal__eyebrow">Teenistusse</p>
                        <h2 className="vm-modal__title" id="vm-login-title">Sisene mängu</h2>
                    </div>

                    <form onSubmit={handleLogin} className="vm-form">
                        <div className="vm-field">
                            <label className="vm-field__label" htmlFor="vm-login-email">
                                E-posti aadress
                            </label>
                            <input
                                id="vm-login-email"
                                ref={firstFieldRef}
                                type="email"
                                placeholder="sinu@email.ee"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="vm-input"
                                disabled={loading}
                                autoComplete="email"
                            />
                        </div>

                        <div className="vm-field">
                            <label className="vm-field__label" htmlFor="vm-login-password">
                                Parool
                            </label>
                            <div className="vm-input-wrap">
                                <input
                                    id="vm-login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="vm-input"
                                    disabled={loading}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="vm-input-affix"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={loading}
                                    aria-label={showPassword ? 'Peida parool' : 'Näita parooli'}
                                >
                                    {showPassword ? 'Peida' : 'Näita'}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <p className="vm-alert" role="alert">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            className="vm-submit"
                            disabled={loading || !email || !password}
                        >
                            {loading ? <><span className="vm-spinner" />Sisenen…</> : 'Logi sisse'}
                        </button>

                        <button
                            type="button"
                            className="vm-linkbtn"
                            onClick={() => setShowForgotPassword(true)}
                            disabled={loading}
                        >
                            Unustasid parooli?
                        </button>
                    </form>

                    {onSwitchToRegister && (
                        <p className="vm-modal__foot">
                            Pole veel kontot?{' '}
                            <button type="button" className="vm-linkbtn" onClick={onSwitchToRegister}>
                                Loo konto
                            </button>
                        </p>
                    )}
                </div>
            </div>

            <ForgotPasswordModal
                isOpen={showForgotPassword}
                onClose={() => setShowForgotPassword(false)}
            />
        </>
    );
};
